/**
 * Долг: кредит, рассрочка или личный — один лист на настройку, «+» в «Долгах»,
 * подсказку нового платежа и правку.
 *
 * Прежде кредит заводился анкетой из девяти подписанных полей и трёх абзацев,
 * а правился другой формой. Теперь лист устроен как правка операции (макет
 * docs/design/loan-form-2026-10-09): остаток крупно, условия строками одной
 * карточки, необязательное свёрнуто в «Ещё условия». Посчитанное — сколько
 * процентов в год и когда закроется — стоит плашками сразу под суммой.
 *
 * Три правила, каждое из которых прежде нарушалось молча:
 *
 * · **Пустая ставка — не ноль.** Стёртые 12% превращались в беспроцентный
 *   долг: он уезжал в конец очереди гашения без единого рубля процентов.
 * · **Убывающий график не считается аннуитетом.** Без числа оставшихся
 *   платежей тело делить не на что, и лист говорит об этом, а не показывает
 *   чужие числа.
 * · **Платёж долга в «Платежах» один.** Платёж с тем же названием становится
 *   платежом долга, а не дублируется; правка долга меняет и его.
 */
import { useState } from 'react';
import {
  ZERO, accrualStart, add, amortize, dayInterest, formatMoney, kop, loanProgress, progressLabel, nextOccurrenceInclusive,
  roundHalfEven, type Account, type Loan, type Money, type Obligation, type PlainDate,
} from '@skoka/core';
import { AmountInput } from './amount.js';
import { CardAccount, CardDate, CardInput, CardMoney, CardPick, CardToggle } from './card-fields.js';
import { Icon } from './icon.js';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { dayMonth, plural } from './text.js';
import {
  addLoan, formatAmountInput, loanPayDate as storedPayDate, parseAmountInput, parseRateInput,
  syncLoanPayment, todayPd, updateLoan, upsertLoanPayment,
} from './store.js';
import { DateButton } from './date-picker.js';

/**
 * Долг в черновике — то, что правит лист.
 *
 * Ипотека, автокредит, рассрочка и долг знакомому — один вид записи. Платёж
 * по нему становится обязательством, но заводится один раз: долг им владеет,
 * иначе тот же платёж оказался бы в плане дважды.
 */
export interface DLoan {
  name: string;
  kind: 'amortizing' | 'installment' | 'private';
  /** Остаток **основного долга**, а не общая задолженность с процентами. */
  principal: string;
  /** Дата, на которую назван остаток: без неё срок не от чего считать. */
  principalAsOf: string;
  /**
   * Когда долг взят.
   *
   * Отвечает на вопрос, которого не видно по остатку: свежий это долг или
   * почти выплаченный. Автокредит на 400 000 ₽ месячной давности и ипотека с
   * тем же остатком на двадцатом году — разные истории.
   */
  openedAt: string;
  rate: string;
  payment: string;
  /** Число месяца платежа — то же, что у даты ниже; держится ради старых черновиков. */
  day: string;
  /**
   * Дата следующего платежа. Числа месяца мало: первый платёж может быть и в
   * следующем месяце. Нет в черновиках, сохранённых до появления поля.
   */
  payDate?: string;
  /** Последний платёж по договору; у личного долга — «вернуть до». */
  endDate: string;
  /** Аннуитет или дифференцированный: экономия от досрочного взноса разная. */
  schedule: 'annuity' | 'differentiated';
  /**
   * Сколько платежей осталось. Нужно убывающему графику: тело долга делится
   * поровну именно на них, и без этого числа срок считать нечем.
   */
  left: string;
  allowsEarly: boolean;
  /**
   * Личный долг возвращают частями. Нет — платежа в «Платежах» нет: деньги
   * отдадут разом, к «вернуть до». У кредита и рассрочки платёж есть всегда.
   */
  parts?: boolean;
  /** Когда банк принимает досрочный. Пусто — в любой день. */
  earlyRule?: 'any' | 'payment_day';
  /**
   * Сумма введена уже с процентами, набежавшими с последнего платежа, — как
   * «сумма для полного погашения» в приложении банка. Хранится основной долг:
   * проценты SKOKA отделяет сама, иначе они начислились бы второй раз.
   */
  withInterest?: boolean;
  /** Платить со счёта — в черновике настройки, ссылкой `a2`. */
  accountId?: string;
  /**
   * Найден на скриншоте банка: банк и виден ли там график. По ним плашка
   * называет, чего не хватает. Проверен человеком — пусто.
   */
  shot?: { bank: string | null; scheduleKnown: boolean; kind: string };
}

const isDate = (v: string | undefined): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v ?? '');
const dayNum = (sv: string): number => Math.min(31, Math.max(1, Math.trunc(Number(sv) || 1)));
const money = (s: string): Money => parseAmountInput(s);

/**
 * Проценты, набежавшие к дате остатка: с последнего платежа по графику, но не
 * раньше выдачи. От них зависит, что SKOKA покажет под суммой и что сохранит,
 * если сумма введена уже с процентами. `null` — посчитать не из чего: нет
 * ставки, платежа или даты.
 */
export function draftAccrual(l: DLoan, today: PlainDate): { since: PlainDate; asOf: PlainDate; body: Money; interest: Money } | null {
  const rate = parseRateInput(l.rate) ?? 0;
  const entered = money(l.principal);
  if (rate <= 0 || entered <= ZERO || !paysMonthly(l)) return null;
  const asOf = (isDate(l.principalAsOf) ? l.principalAsOf : today) as PlainDate;
  const since = accrualStart({
    principalAsOf: asOf, paymentDay: Number(loanPayDate(l, today).slice(8, 10)),
    ...(isDate(l.openedAt) ? { openedAt: l.openedAt as PlainDate } : {}),
  });
  if (!since || since >= asOf) return null;
  // Сумма с процентами = тело × (1 + доля процентов за эти дни).
  const share = dayInterest(1, rate, since, asOf);
  const body = l.withInterest ? kop(roundHalfEven(entered / (1 + share))) : entered;
  return { since, asOf, body, interest: kop(roundHalfEven(body * share)) };
}

export const newLoan = (name: string, kind: DLoan['kind'], rate = ''): DLoan => ({
  name, kind, principal: '', principalAsOf: todayPd(), openedAt: '', rate, payment: '', day: '10',
  payDate: nextOccurrenceInclusive(todayPd(), 10), left: '',
  endDate: '', schedule: 'annuity', allowsEarly: true,
  ...(kind === 'private' ? { parts: false } : {}),
});

/** Дата платежа долга из черновика: старый черновик знает только число. */
export const loanPayDate = (l: DLoan, today: PlainDate): PlainDate =>
  isDate(l.payDate) ? (l.payDate as PlainDate) : nextOccurrenceInclusive(today, dayNum(l.day));

/** Есть ли у долга платёж в «Платежах». У личного — только если частями. */
export const paysMonthly = (l: DLoan): boolean => l.kind !== 'private' || l.parts === true;

/**
 * Поля долга из черновика — для создания и для правки.
 *
 * Необязательное, которое человек стёр, возвращается списком `removed`:
 * пустая дата — ответ «не знаю», а не «оставь как было».
 */
export function draftLoanFields(l: DLoan, today: PlainDate): {
  fields: Omit<Loan, 'id' | 'obligationId' | 'accountId'>;
  removed: (keyof Loan)[];
} {
  const pays = paysMonthly(l);
  const payDate = loanPayDate(l, today);
  const left = l.schedule === 'differentiated' && /^\d{1,3}$/.test(l.left.trim()) ? Number(l.left.trim()) : null;
  const fields: Omit<Loan, 'id' | 'obligationId' | 'accountId'> = {
    name: l.name.trim(),
    kind: l.kind,
    // Введено с процентами — храним основной долг, проценты отделены.
    principal: l.withInterest ? (draftAccrual(l, today)?.body ?? money(l.principal)) : money(l.principal),
    // Пустая ставка — «не знаю», а не беспроцентный долг. Сохранение такого
    // кредита остановлено в листе; у личного долга пустая значит «без процентов».
    ratePct: parseRateInput(l.rate) ?? 0,
    payment: pays ? money(l.payment) : ZERO,
    paymentDay: Number(payDate.slice(8, 10)),
    allowsEarly: l.kind === 'private' ? true : l.allowsEarly,
    // Дата остатка и тип графика — не украшение: без них нельзя ни назвать
    // срок, ни посчитать, что даст досрочный взнос.
    ...(isDate(l.principalAsOf) ? { principalAsOf: l.principalAsOf as PlainDate } : {}),
    ...(isDate(l.openedAt) ? { openedAt: l.openedAt as PlainDate } : {}),
    ...(left !== null ? { termMonths: left } : {}),
    ...(l.kind === 'amortizing' ? { schedule: l.schedule } : {}),
    ...(isDate(l.endDate) ? { endDate: l.endDate as PlainDate } : {}),
    ...(l.earlyRule === 'payment_day' && l.kind !== 'private' ? { earlyRule: 'payment_day' as const } : {}),
  };
  const removed: (keyof Loan)[] = [
    ...(l.earlyRule === 'payment_day' && l.kind !== 'private' ? [] : ['earlyRule' as const]),
    ...(isDate(l.principalAsOf) ? [] : ['principalAsOf' as const]),
    ...(isDate(l.openedAt) ? [] : ['openedAt' as const]),
    ...(left !== null ? [] : ['termMonths' as const]),
    ...(l.kind === 'amortizing' ? [] : ['schedule' as const]),
    ...(isDate(l.endDate) ? [] : ['endDate' as const]),
  ];
  return { fields, removed };
}

/**
 * Сохранить новый долг: платёж в «Платежах» и долг, который им владеет.
 *
 * Один путь для настройки, «+» в «Долгах» и подсказки нового платежа: долг,
 * добавленный позже, считается так же, как заведённый в начале.
 *
 * `target` — какой платёж взять: не задан — с тем же названием, если есть;
 * `'new'` — новый; иначе — выбранный в листе.
 */
export async function saveDraftLoan(l: DLoan, accountId: string, target?: string): Promise<Loan> {
  const today = todayPd();
  const { fields } = draftLoanFields(l, today);
  if (!paysMonthly(l) || fields.payment <= ZERO) {
    // Личный долг без графика: платежа нет, деньги отдают разом.
    return addLoan({ ...fields, accountId });
  }
  const ob = await upsertLoanPayment({
    name: fields.name, accountId, amount: fields.payment, payDate: loanPayDate(l, today),
    ...(fields.endDate ? { endDate: fields.endDate } : {}),
    ...(target ? { target } : {}),
  });
  return addLoan({ ...fields, obligationId: ob.id, accountId });
}

/** Черновик из сохранённого долга — для правки тем же листом. */
export function loanToDraft(loan: Loan, obligations: readonly Obligation[], today: PlainDate): DLoan {
  const kind: DLoan['kind'] = loan.kind === 'credit_card' ? 'amortizing' : loan.kind;
  const payDate = storedPayDate(loan, obligations, today);
  return {
    name: loan.name,
    kind,
    principal: formatAmountInput(String(loan.principal / 100)),
    principalAsOf: loan.principalAsOf ?? '',
    openedAt: loan.openedAt ?? '',
    rate: String(loan.ratePct).replace('.', ','),
    payment: loan.payment > ZERO ? formatAmountInput(String(loan.payment / 100)) : '',
    day: String(Number(payDate.slice(8, 10))),
    payDate,
    endDate: loan.endDate ?? '',
    schedule: loan.schedule ?? 'annuity',
    left: loan.termMonths ? String(loan.termMonths) : '',
    allowsEarly: loan.allowsEarly,
    ...(kind === 'private' ? { parts: loan.payment > ZERO } : {}),
    ...(loan.earlyRule ? { earlyRule: loan.earlyRule } : {}),
  };
}

/**
 * Сохранить правку долга: сам долг и его платёж в «Платежах».
 *
 * Сумма, дата, срок и счёт платежа меняются вместе с долгом — прежде правка
 * меняла только «Долги», а из «Доступно» вычиталась старая сумма. Личный долг,
 * который перестали возвращать частями, теряет платёж; начали — получает.
 */
export async function saveLoanEdit(loan: Loan, v: DLoan, accountId?: string): Promise<void> {
  const today = todayPd();
  if (loan.kind === 'credit_card') {
    // Долг, лимит и дату платежа карты ведёт сама карта.
    const rate = parseRateInput(v.rate);
    await updateLoan(loan.id, {
      name: v.name.trim() || loan.name, allowsEarly: v.allowsEarly,
      ...(rate !== null ? { ratePct: rate } : {}),
      ...(isDate(v.openedAt) ? { openedAt: v.openedAt as PlainDate } : {}),
    }, isDate(v.openedAt) ? [] : ['openedAt']);
    return;
  }
  const { fields, removed } = draftLoanFields(v, today);
  await updateLoan(loan.id, { ...fields, name: fields.name || loan.name }, removed);
  await syncLoanPayment(loan.id, loanPayDate(v, today), accountId);
}

/** «12 лет 11 мес» — срок словами. */
const termText = (months: number): string => {
  const y = Math.floor(months / 12);
  const m = months % 12;
  return [y ? plural(y, 'год', 'года', 'лет') : '', m ? `${m} мес` : ''].filter(Boolean).join(' ');
};

const KIND_TITLE: Record<DLoan['kind'], string> = {
  amortizing: 'Кредит', installment: 'Рассрочка', private: 'Личный долг',
};

export interface PaymentChoice {
  id: string;
  title: string;
}

export function DebtSheet({
  init, isNew, title, accounts, accountId: initAccount, payments, linkedTitle, card, today,
  onSave, onDelete, onClose, lead,
}: {
  /** Плашка сверху листа: что пришло со скриншота и чего не хватает. */
  lead?: React.ReactNode;
  init: DLoan;
  isNew: boolean;
  /** Заголовок правки — название долга. У нового — «Новый долг». */
  title?: string;
  /** Счета для платежа. Нет — строки нет: в настройке счета ещё не созданы. */
  accounts?: Account[];
  accountId?: string;
  /** Свободные платежи, с которыми можно связать новый долг. Нет — строки нет. */
  payments?: readonly Obligation[];
  /** Правка: с каким платежом связан долг. */
  linkedTitle?: string;
  /** Кредитка: долг, лимит и дату ведёт карта, здесь их не правят. */
  card?: { debt: Money };
  today: PlainDate;
  onSave: (v: DLoan, extra: { accountId?: string; target?: string }) => void | Promise<void>;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const [v, setV] = useState<DLoan>(init);
  const [acc, setAcc] = useState(initAccount ?? accounts?.[0]?.id ?? '');
  /** Выбор платежа человеком; `null` — по названию. */
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const on = <K extends keyof DLoan>(k: K, x: DLoan[K]) => setV((p) => ({ ...p, [k]: x }));

  const priv = v.kind === 'private';
  const pays = !card && paysMonthly(v);
  const payDate = loanPayDate(v, today);
  /** `null` — ставка не названа. У личного долга пустая — без процентов. */
  const ratePct = parseRateInput(v.rate) ?? (priv ? 0 : null);
  const principal = card ? card.debt : money(v.principal);
  /*
    Проценты, набежавшие с последнего платежа. Под суммой сказано, как SKOKA
    её поняла, — основной долг или уже с процентами, — и как поправить одним
    тапом. Посчитанное ниже — от основного долга.
  */
  const accrual = !card && !priv ? draftAccrual(v, today) : null;
  const body = accrual ? accrual.body : principal;
  const [ruleOpen, setRuleOpen] = useState(false);
  const payment = pays ? money(v.payment) : ZERO;
  const leftMonths = /^\d{1,3}$/.test(v.left.trim()) ? Number(v.left.trim()) : null;

  // Остаток, названный до выдачи, — противоречие: одна из дат неверна.
  const datesConflict = isDate(v.openedAt) && isDate(v.principalAsOf) && v.openedAt > v.principalAsOf;
  const needLeft = !card && v.kind === 'amortizing' && v.schedule === 'differentiated' && leftMonths === null;
  // Срок раньше платежа — ряд платежей пуст, и платёж исчез бы молча.
  const endTooEarly = pays && isDate(v.endDate) && v.endDate < payDate;
  const rateMissing = !card && ratePct === null;

  /** Платёж, с которым свяжется новый долг: выбранный или с тем же названием. */
  const sameName = payments?.find((o) => o.name.trim().toLowerCase() === v.name.trim().toLowerCase());
  const target = pick ?? sameName?.id ?? 'new';

  // Посчитанное — плашками под суммой.
  const chips: string[] = [];
  if (principal > ZERO && ratePct !== null) {
    // Целыми рублями: копейки от отделённых процентов здесь только мешают.
    const yearly = card ? ZERO : kop(Math.round((body * ratePct) / 10_000) * 100);
    chips.push(yearly > ZERO ? `≈ ${formatMoney(yearly)} процентов в год` : card ? '' : 'без процентов');
  }
  const months = card || ratePct === null || needLeft || !pays || payment <= ZERO ? null : amortize({
    principal: body, ratePct, payment,
    kind: v.kind === 'amortizing' ? v.schedule : 'annuity',
    ...(isDate(v.principalAsOf) ? { from: v.principalAsOf as PlainDate } : {}),
    ...(v.kind === 'amortizing' && v.schedule === 'differentiated' && leftMonths ? { termMonths: leftMonths } : {}),
  }).months;
  if (months !== null && months > 0) chips.push(`закроется ≈ через ${termText(months)}`);
  const noPayoff = pays && payment > ZERO && ratePct !== null && ratePct > 0 && months === null && !needLeft
    && payment <= kop(Math.round((body * ratePct) / 1200));
  // Без процентов срок — долг, делённый на платёж; дальше 50 лет — почти
  // наверняка опечатка в платеже, а не настоящий график.
  const tooLong = pays && payment > ZERO && ratePct === 0 && months === null && !needLeft;
  if (!isNew && isDate(v.openedAt) && !datesConflict) {
    const p = progressLabel(loanProgress({
      loan: { id: '', name: v.name, kind: v.kind, principal: body, ratePct: ratePct ?? 0, payment, paymentDay: 1, allowsEarly: true, openedAt: v.openedAt as PlainDate },
      today, monthsLeft: null,
    }));
    if (p) chips.push(p.toLowerCase());
  }

  // «Ещё условия» открыты сразу, если там уже что-то названо: иначе правка
  // прятала бы условия, которые человек ввёл сам.
  const [more, setMore] = useState(() => (!priv && isDate(v.endDate)) || isDate(v.openedAt)
    || (v.kind === 'amortizing' && v.schedule === 'differentiated') || !v.allowsEarly || v.earlyRule === 'payment_day');
  const moreOpen = more || datesConflict || needLeft;

  const ready = !busy && v.name.trim() !== '' && (card ? true : principal > ZERO)
    && !rateMissing && !datesConflict && !needLeft && !endTooEarly
    && (!pays || (payment > ZERO && isDate(payDate)))
    && (!pays || !accounts || acc !== '');

  const head = isNew ? 'Новый долг' : (title ?? v.name) || 'Долг';

  return (
    <SheetWrap title={head} onClose={onClose} className="rcpt"
      {...(card ? { tip: 'Долг, лимит и дату платежа карты меняют в «Деньгах»: нажми на карту в «Долгах».' } : {})}>
      {lead}
      <div className="rcform">
        {!card && (
          <div className="segs" role="group" aria-label="Вид долга">
            {(['amortizing', 'installment', 'private'] as const).map((k) => (
              <button key={k} className={`seg${v.kind === k ? ' on' : ''}`} aria-pressed={v.kind === k}
                onClick={() => setV((p) => ({
                  ...p, kind: k,
                  // У рассрочки и личного долга процентов обычно нет — ставим
                  // ноль, но поле остаётся: бывает и под процент.
                  ...(k !== 'amortizing' && p.rate.trim() === '' ? { rate: '0' } : {}),
                  ...(k === 'private' && p.parts === undefined ? { parts: false } : {}),
                }))}>{KIND_TITLE[k]}</button>
            ))}
          </div>
        )}

        <div className="rcamounthead">
          <span>{card ? 'Долг по карте' : priv ? 'Сколько должен' : v.withInterest ? 'Долг с процентами' : 'Остаток основного долга'}</span>
          {!priv && !card && (v.withInterest
            ? <Tip>Сумма из приложения банка вместе с процентами, набежавшими с последнего платежа.
              SKOKA отделит проценты и сохранит основной долг — второй раз они не начислятся.</Tip>
            : <Tip>Основной долг из справки или приложения банка, без начисленных процентов
              и комиссий. Справа — на какую дату названо это число.</Tip>)}
          <span className="grow" />
          {!priv && !card && (
            <label className="rcasof">
              {isDate(v.principalAsOf) && v.principalAsOf !== today ? `на ${dayMonth(v.principalAsOf, today)}` : 'на сегодня'}
              <Icon name="chevron-right" size={11} className="rcchev" />
              <DateButton overlay label="На какую дату известен остаток" value={v.principalAsOf} max={today}
                onChange={(d) => on('principalAsOf', d)} />
            </label>
          )}
        </div>
        {card ? (
          <div className="rcamountedit readonly"><span className="num">{formatMoney(card.debt)}</span></div>
        ) : (
          <label className="rcamountedit">
            <AmountInput value={v.principal} onChange={(x) => on('principal', x)} placeholder="0"
              label={priv ? 'Сколько должен' : v.withInterest ? 'Долг с процентами' : 'Остаток основного долга'} className="num" />
            <span aria-hidden="true">₽</span>
          </label>
        )}
        {accrual && (
          <div className="intnote">
            {v.withInterest
              ? `Из них проценты с ${dayMonth(accrual.since, today)} — ${formatMoney(accrual.interest)}, основной долг ${formatMoney(accrual.body)}. `
              : `Плюс проценты с ${dayMonth(accrual.since, today)} — ${formatMoney(accrual.interest)}, долг ${accrual.asOf === today ? 'на сегодня' : `на ${dayMonth(accrual.asOf, today)}`} ${formatMoney(add(accrual.body, accrual.interest))}. `}
            <button className="lnk" onClick={() => on('withInterest', !v.withInterest)}>
              {v.withInterest ? 'Это без процентов' : 'Это уже с процентами'}</button>
          </div>
        )}

        {chips.filter(Boolean).length > 0 && (
          <div className="rcchips">{chips.filter(Boolean).map((c) => <span key={c} className="rcinfo">{c}</span>)}</div>
        )}
        {rateMissing && (money(v.principal) > ZERO || payment > ZERO) && (
          <div className="warnline bad">
            Ставка не названа. Ноль процентов — это рассрочка, а не «не знаю»: впиши ставку
            из договора. Если процентов правда нет, поставь 0.
          </div>
        )}
        {noPayoff && (
          <div className="warnline bad">Платёж не покрывает проценты: так долг не гасится.</div>
        )}
        {tooLong && (
          <div className="warnline bad">При таком платеже долг гасится дольше 50 лет — проверь сумму платежа.</div>
        )}

        <div className="rccard rcsel">
          <CardInput label={priv ? 'Кому' : 'Название'} value={v.name} onChange={(x) => on('name', x)}
            placeholder={priv ? 'Сергей' : 'Ипотека'} width={170} />
          {priv && (
            <CardDate label="Вернуть до" value={v.endDate} min={today} clear="Не договорились"
              text={isDate(v.endDate) ? dayMonth(v.endDate, today) : 'не договорились'}
              onChange={(x) => on('endDate', x)} />
          )}
          {priv && (
            <CardToggle label="Возвращаю частями" checked={v.parts === true} onChange={(x) => on('parts', x)} />
          )}
          {pays && (
            <CardMoney label="Платёж в месяц" value={v.payment} onChange={(x) => on('payment', x)} placeholder="12 000" />
          )}
          {pays && (
            <CardDate label="Следующий платёж" value={payDate} sub="дальше каждый месяц"
              text={dayMonth(payDate, today)}
              onChange={(x) => setV((p) => ({ ...p, payDate: x, ...(isDate(x) ? { day: String(Number(x.slice(8, 10))) } : {}) }))} />
          )}
          <CardInput label="Ставка" value={v.rate} onChange={(x) => on('rate', x)} unit="% годовых"
            inputMode="decimal" placeholder={priv ? '0' : '12'} width={56}
            {...(ratePct === 0 && !priv ? { tip: 'Ноль процентов — досрочно закрывать такой долг незачем: в очереди гашения он окажется последним.' } : {})} />
          {pays && accounts && accounts.length > 0 && (
            <CardAccount label="Платить со счёта" pickerTitle="Платить со счёта" accounts={accounts}
              value={acc} onChange={setAcc} />
          )}
          {pays && isNew && payments && (
            <CardPick label="В «Платежах»" value={target} onChange={setPick}
              options={[
                ...payments.map((o) => ({ id: o.id, title: `${o.name} · уже есть` })),
                { id: 'new', title: 'Новый платёж' },
              ]} />
          )}
          {pays && !isNew && linkedTitle && (
            <div className="rcselrow"><span className="rclabel">В «Платежах»
              <Tip>Сумма, дата и срок платежа меняются вместе с долгом.</Tip></span>
              <span className="rcvalue"><span className="rcvaltext">{linkedTitle}</span></span></div>
          )}
        </div>

        <button className={`rcview more${moreOpen ? ' on' : ''}`} aria-expanded={moreOpen}
          onClick={() => setMore(!moreOpen)}>
          Ещё условия <Icon name="chevron-down" size={12} />
        </button>
        {moreOpen && (
          <div className="rccard rcsel">
            {!priv && !card && (
              <CardDate label="До какой даты" value={v.endDate} min={payDate} clear="Не указана"
                tip="Последний платёж по договору. После этой даты платёж уйдёт из «Платежей»."
                text={isDate(v.endDate) ? dayMonth(v.endDate, today) : 'не указана'}
                onChange={(x) => on('endDate', x)} />
            )}
            <CardDate label={priv ? 'Когда занял' : 'Когда взяли'} value={v.openedAt} max={today} clear="Не указана"
              tip="Отличает свежий долг от взятого давно. На очередь гашения не влияет: уплаченного не вернуть."
              text={isDate(v.openedAt) ? dayMonth(v.openedAt, today) : 'не указана'}
              onChange={(x) => on('openedAt', x)} />
            {v.kind === 'amortizing' && !card && (
              <CardPick label="График" value={v.schedule} onChange={(x) => on('schedule', x as DLoan['schedule'])}
                options={[{ id: 'annuity', title: 'Равный платёж' }, { id: 'differentiated', title: 'Убывающий' }]} />
            )}
            {v.kind === 'amortizing' && v.schedule === 'differentiated' && !card && (
              <CardInput label="Платежей осталось" value={v.left} onChange={(x) => on('left', x)}
                inputMode="numeric" placeholder="60" width={70}
                tip="Тело долга делится поровну на эти платежи, проценты убывают вместе с остатком." />
            )}
            {!priv && (
              <CardToggle label="Можно гасить досрочно" checked={v.allowsEarly} onChange={(x) => on('allowsEarly', x)} />
            )}
            {!priv && !card && v.allowsEarly && (
              <button className="rcselrow" onClick={() => setRuleOpen(true)}
                aria-label={`Банк принимает досрочный: ${v.earlyRule === 'payment_day' ? 'только в день платежа' : 'в любой день'}`}>
                <span className="rclabel">Банк принимает досрочный</span>
                <span className="rcvalue"><span className="rcvaltext">
                  {v.earlyRule === 'payment_day' ? 'в день платежа' : 'в любой день'}</span>
                  <Icon name="chevron-right" size={13} className="rcchev" /></span>
              </button>
            )}
          </div>
        )}
        {ruleOpen && (
          <SheetWrap title="Банк принимает досрочный" onClose={() => setRuleOpen(false)}>
            <div className="fieldlist account-list">
              {([
                ['any', 'В любой день', 'проценты начисляются каждый день — чем раньше внести, тем меньше процентов'],
                ['payment_day', 'Только в день платежа', 'досрочный спишут вместе с платежом по графику'],
              ] as const).map(([id, name, sub]) => {
                const chosen = (v.earlyRule ?? 'any') === id;
                return (
                  <button key={id} className="li w" aria-pressed={chosen}
                    onClick={() => { on('earlyRule', id); setRuleOpen(false); }}>
                    <span className="nm"><span className="account-name">{name}</span><span className="wrap">{sub}</span></span>
                    <span>{chosen ? <Icon name="check" size={14} /> : null}</span>
                  </button>
                );
              })}
            </div>
            <p className="sheetnote">Как у твоего банка — в договоре или в приложении банка, раздел «Досрочное погашение».</p>
          </SheetWrap>
        )}
        {datesConflict && (
          <div className="warnline bad">
            Остаток назван на дату раньше, чем взят долг, — поправь одну из дат.
          </div>
        )}
        {needLeft && (
          <div className="warnline bad">
            Без числа оставшихся платежей срок и проценты по убывающему графику посчитать нечем.
          </div>
        )}
        {endTooEarly && (
          <div className="warnline bad">Срок раньше следующего платежа — поправь одну из дат.</div>
        )}

        <button className="btn pri" disabled={!ready}
          onClick={async () => {
            setBusy(true);
            try {
              await onSave({ ...v, name: v.name.trim() }, {
                ...(pays && accounts ? { accountId: acc } : {}),
                ...(pays && isNew && payments ? { target } : {}),
              });
            } finally { setBusy(false); }
          }}>{isNew ? 'Добавить' : 'Сохранить'}</button>
        {/* Удалять нечего, пока долг не добавлен. */}
        {onDelete && !isNew && (
          <button className="textact danger" disabled={busy} onClick={onDelete}>Удалить долг</button>
        )}
      </div>
    </SheetWrap>
  );
}

/** Подпись долга в списке настройки: то, что человек хочет проверить взглядом. */
export function loanDraftDetail(l: DLoan): string {
  const parts = [`${l.kind === 'private' ? 'должен' : 'остаток'} ${formatMoney(money(l.principal))}`];
  const rate = parseRateInput(l.rate) ?? 0;
  if (l.kind !== 'private' || rate > 0) parts.push(rate > 0 ? `${l.rate}% годовых` : 'без процентов');
  if (paysMonthly(l)) parts.push(`${formatMoney(money(l.payment))} ${dayNum(l.day)}-го`);
  else if (isDate(l.endDate)) parts.push(`вернуть до ${dayMonth(l.endDate, todayPd())}`);
  return parts.join(' · ');
}
